import { useQuery } from '@tanstack/react-query';
import { useRequestSections } from '../../../api/request';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/Store';
import { setDecisionData } from '../../../redux/reducer/contextIdReducer';
import { PersistHelper } from '../../../redux/persistHelper';
import customNotifications from './customNotifications';
import { Flows } from '../../../utils/enum_constants/decision';
import { useState } from 'react';

const useDecision = () => {
  const { Audit_Failure, Current_Answer, Decision, Prior_Decisions, Seeded } =
    useRequestSections();
  const dispatch = useDispatch();
  const contextId = useSelector(
    (state: RootState) => state.contextId.contextId
  );
  const isAudit = useSelector((state: RootState) => state.contextId.isAudit);
  const contextObj = useSelector(
    (state: RootState) => state.contextId.queryObj
  );

  const [isErrorCoreScriptInitData, setIsError] = useState(false);

  const shouldCallHappen =
    isAudit && contextObj?.context !== Flows?.Textrequest;

  const {
    data: decisionReasons,
    isLoading: isDecisionReasonsLoading,
    isFetching: isDecisionReasonsFetching,
    refetch,
    error: decisionReasonsError,
    isSuccess: decisionReasonsSuccessful,
  } = useQuery({
    queryKey: ['decisionReasons', contextId],
    queryFn: async () => {
      const isPersisted = await PersistHelper.isDataPersisted(contextId);
      if (!isPersisted && shouldCallHappen) {
        try {
          const response = await Decision?.getDecisionReasons(contextId);
          dispatch(setDecisionData(response?.data?.decisions));
          return response?.data;
        } catch (error: any) {
          const errorCode =
            error?.response?.status || error?.code || 'Unknown Error';
          customNotifications('Decision Reasons', errorCode);
          setIsError(true);
          //PersistHelper?.clearAllData();
        }
      }
      return null;
    },
    enabled: !!contextId,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const {
    data: SeededData,
    isLoading: isSeededLoading,
    isFetching: isSeededFetching,
    refetch: refetchSeededData,
    error: SeededDataError,
    isSuccess: SeededDataSuccess,
  } = useQuery({
    queryKey: ['Seeded', contextId],
    queryFn: async () => {
      const isPersisted = await PersistHelper.isDataPersisted(contextId);
      if (!isPersisted) {
        try {
          const response = await Seeded?.getSeeded(contextId);
          return response?.data;
        } catch (error: any) {
          const errorCode =
            error?.response?.status || error?.code || 'Unknown Error';
          customNotifications('Seeded Data', errorCode);
          setIsError(true);
          //PersistHelper?.clearAllData();
        }
      }
    },
    enabled: !!contextId,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const {
    data: currentAnswers,
    isLoading: isCurrentAnswersLoading,
    isFetching: isCurrentAnswersFetching,
    refetch: refetchCurrentAnswers,
    error: currentAnswersError,
    isSuccess: currentAnswersSuccess,
  } = useQuery({
    queryKey: ['Current', contextId],
    queryFn: async () => {
      const isPersisted = await PersistHelper.isDataPersisted(contextId);
      if (!isPersisted && shouldCallHappen) {
        try {
          const response = await Current_Answer?.getCurrentAnswers(contextId);
          return response?.data;
        } catch (error: any) {
          const errorCode =
            error?.response?.status || error?.code || 'Unknown Error';
          customNotifications('Current Answers', errorCode);
          setIsError(true);
          //PersistHelper?.clearAllData();
        }
      }
      return null;
    },
    enabled: !!contextId,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const {
    data: auditFailures,
    isLoading: isAuditFailuresLoading,
    isFetching: isAuditFailuresFetching,
    refetch: refetchAuditFailures,
    error: auditFailuresError,
    isSuccess: auditFailuresSuccess,
  } = useQuery({
    queryKey: ['AuditFailures', contextId],
    queryFn: async () => {
      const isPersisted = await PersistHelper.isDataPersisted(contextId);
      if (!isPersisted && shouldCallHappen) {
        try {
          const response = await Audit_Failure?.getAuditFailures(contextId);
          return response?.data;
        } catch (error: any) {
          const errorCode =
            error?.response?.status || error?.code || 'Unknown Error';
          customNotifications('Audit Failures', errorCode);
          setIsError(true);
          //PersistHelper?.clearAllData();
        }
      }
      return null;
    },
    enabled: !!contextId,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const {
    data: priorDecisions,
    isLoading: isPriorDecisionsLoading,
    isFetching: isPriorDecisionsFetching,
    refetch: refetchPriorDecisions,
    error: priorDecisionsError,
    isSuccess: priorDecisionsSuccess,
  } = useQuery({
    queryKey: ['PriorDecisions', contextId],
    queryFn: async () => {
      const isPersisted = await PersistHelper.isDataPersisted(contextId);
      if (!isPersisted && shouldCallHappen) {
        try {
          const response = await Prior_Decisions?.getPriorDecisions(contextId);
          return response?.data;
        } catch (error: any) {
          const errorCode =
            error?.response?.status || error?.code || 'Unknown Error';
          customNotifications('Prior Decisions', errorCode);
          setIsError(true);
          //PersistHelper?.clearAllData();
        }
      }
      return null;
    },
    enabled: !!contextId,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  return {
    decisionReasons,
    isDecisionReasonsFetching,
    isDecisionReasonsLoading,
    refetch,
    decisionReasonsSuccessful,
    decisionReasonsError,

    SeededData,
    isSeededFetching,
    isSeededLoading,
    refetchSeededData,
    SeededDataSuccess,
    SeededDataError,

    currentAnswers,
    currentAnswersError,
    isCurrentAnswersFetching,
    refetchCurrentAnswers,
    currentAnswersSuccess,
    isCurrentAnswersLoading,

    auditFailures,
    isAuditFailuresFetching,
    isAuditFailuresLoading,
    refetchAuditFailures,
    auditFailuresSuccess,
    auditFailuresError,

    priorDecisions,
    priorDecisionsError,
    refetchPriorDecisions,
    isPriorDecisionsFetching,
    priorDecisionsSuccess,
    isPriorDecisionsLoading,

    isErrorCoreScriptInitData,
  };
};

export default useDecision;
